import { useContext, useRef, useState } from 'react';
import { ThemeContext } from '@/base/theme/ThemeProvider';
import MoonIcon from '@mui/icons-material/DarkModeOutlined';
import ExpandMoreTwoToneIcon from '@mui/icons-material/ExpandMoreTwoTone';
import LockOpenTwoToneIcon from '@mui/icons-material/LockOpenTwoTone';
import SunIcon from '@mui/icons-material/WbSunnyOutlined';
import {
  Avatar,
  Box,
  Button,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  Typography,
  styled,
  useTheme
} from '@mui/material';
import { useTranslation } from 'next-i18next';
import { useUserStore } from '@/user/stores/userStore';
import LogoutModal from '../../LogoutModal';
import { parseCurrentWorkspaceName } from '@/common/utils/parseWorkspaceName';

const UserBoxButton = styled(Button)(
  ({ theme }) => `
        padding: ${theme.spacing(0, 1)};
        height: ${theme.spacing(7)};
`
);

const MenuUserBox = styled(Box)(
  ({ theme }) => `
        background: ${theme.colors.alpha.black[5]};
        padding: ${theme.spacing(2)};
`
);

const UserBoxText = styled(Box)(
  ({ theme }) => `
    width: 100%;
    text-align: left;
    padding-left: ${theme.spacing(1)};
`
);

const UserBoxLabel = styled(Typography)(
  ({ theme }) => `
        font-weight: ${theme.typography.fontWeightBold};
        color: ${theme.palette.secondary.main};
        display: block;
`
);

const UserBoxDescription = styled(Typography)(
  ({ theme }) => `
        color: ${theme.palette.secondary.light}
`
);
function HeaderUserbox() {
  const { t } = useTranslation();
  const theme = useTheme();
  const {
    profile: { email, name, picture }
  } = useUserStore();

  const setThemeName = useContext(ThemeContext);
  const ref = useRef<any>(null);
  const [isOpen, setOpen] = useState<boolean>(false);
  const [isLogOutOpen, setIsLogoutOpen] = useState<boolean>(false);

  const handleOpen = (event): void => {
    event.preventDefault();

    setOpen(true);
  };

  const handleClose = (event?: any): void => {
    event && event.preventDefault();
    setOpen(false);
  };

  const handleOnLogoutOpen = (event): void => {
    event.preventDefault();
    handleClose();
    setIsLogoutOpen(true);
  };

  const isDark = theme.palette.mode === 'dark';
  const toggleDarkMode = () => {
    setThemeName(isDark ? 'GuudThemeLight' : 'GuudThemeDark');
  };

  return (
    <>
      <UserBoxButton color="primary" ref={ref} onClick={handleOpen}>
        <Avatar variant="rounded" alt={name} src={picture} />
        <Box
          component="span"
          sx={{
            display: { xs: 'none', md: 'inline-block' }
          }}
        >
          <UserBoxText>
            <UserBoxLabel variant="body1">{name || email}</UserBoxLabel>

            <UserBoxDescription variant="body2">
              {parseCurrentWorkspaceName()}
            </UserBoxDescription>
          </UserBoxText>
        </Box>
        <Box
          component="span"
          sx={{
            display: { xs: 'none', sm: 'inline-block' }
          }}
        >
          <ExpandMoreTwoToneIcon
            sx={{
              ml: 1
            }}
          />
        </Box>
      </UserBoxButton>
      <Popover
        disableScrollLock
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        <MenuUserBox
          sx={{
            minWidth: 210
          }}
          display="flex"
        >
          <Avatar variant="rounded" alt={name} src={picture} />
          <UserBoxText>
            <UserBoxLabel variant="body1">{name || email}</UserBoxLabel>

            <UserBoxDescription variant="body2">
              {parseCurrentWorkspaceName()}
            </UserBoxDescription>
          </UserBoxText>
        </MenuUserBox>
        <Divider
          sx={{
            mb: 0
          }}
        />
        <List
          sx={{
            p: 1
          }}
          component="nav"
        >
          <ListItemButton
            onClick={() => {
              toggleDarkMode();
            }}
          >
            <ListItemIcon sx={{ minWidth: 35 }}>
              {isDark ? <SunIcon /> : <MoonIcon />}
            </ListItemIcon>
            <ListItemText
              primary={isDark ? t('Toggle light mode') : t('Toggle dark mode')}
            />
          </ListItemButton>
        </List>
        <Divider />
        <Box m={1}>
          <Button color="primary" fullWidth onClick={handleOnLogoutOpen}>
            <LockOpenTwoToneIcon
              sx={{
                mr: 1
              }}
            />
            {t('Sign out')}
          </Button>
        </Box>
      </Popover>
      <LogoutModal
        handleClose={handleClose}
        isLogOutOpen={isLogOutOpen}
        setIsLogoutOpen={setIsLogoutOpen}
      />
    </>
  );
}

export default HeaderUserbox;
